import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ComplianceFooter from '../components/ComplianceFooter';
import axios from 'axios';
import { LogOut, Search, FileText, Clock, Filter, Bookmark, BookmarkCheck, CheckCircle2, Send, CreditCard, Receipt, ShieldCheck, ShieldAlert, UserCog } from 'lucide-react';
import { toast } from 'sonner';
import ProfilePhotoUploader from '../components/ProfilePhotoUploader';
import MyInvoices from '../components/MyInvoices';
import SupplierBottomNav from '../components/SupplierBottomNav';
import LifecycleProgressBadge from '../components/LifecycleProgressBadge';

const SupplierDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [tab, setTab] = useState('available');
  const [tenders, setTenders] = useState([]);
  const [savedIds, setSavedIds] = useState(new Set());
  const [savedTenders, setSavedTenders] = useState([]);
  const [participatedTenders, setParticipatedTenders] = useState([]);
  const [supplierProfile, setSupplierProfile] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [pendingSuccessFees, setPendingSuccessFees] = useState([]);
  const [paidSuccessFees, setPaidSuccessFees] = useState([]);
  const [filters, setFilters] = useState({ category: '', location: '', minValue: '', maxValue: '' });
  const [loading, setLoading] = useState(true);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = async () => {
    try {
      const [tRes, myRes, profRes, subRes, sfRes] = await Promise.all([
        axios.get(`${API}/tenders?status=published`, { withCredentials: true }),
        axios.get(`${API}/suppliers/my-tenders`, { withCredentials: true }),
        axios.get(`${API}/suppliers/me`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/subscriptions/me`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/supplier/success-fee-invoices`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setTenders(tRes.data || []);
      setSavedTenders(myRes.data?.saved || []);
      setParticipatedTenders(myRes.data?.participated || []);
      setSavedIds(new Set((myRes.data?.saved || []).map(t => t.id)));
      setSupplierProfile(profRes.data);
      setSubscription(subRes.data);
      const fees = Array.isArray(sfRes.data) ? sfRes.data : [];
      setPendingSuccessFees(fees.filter(f => f.status === 'pending'));
      setPaidSuccessFees(fees.filter(f => f.status === 'paid'));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const refetchTenders = async () => {
    try {
      const params = new URLSearchParams({ status: 'published' });
      if (filters.category) params.append('category', filters.category);
      if (filters.location) params.append('location', filters.location);
      if (filters.minValue) params.append('min_value', filters.minValue);
      if (filters.maxValue) params.append('max_value', filters.maxValue);
      const { data } = await axios.get(`${API}/tenders?${params}`, { withCredentials: true });
      setTenders(data || []);
    } catch (e) {
      toast.error('Failed to fetch tenders');
    }
  };

  const toggleBookmark = async (tenderId) => {
    try {
      if (savedIds.has(tenderId)) {
        await axios.delete(`${API}/tenders/${tenderId}/bookmark`, { withCredentials: true });
        toast.success('Removed from My Tenders');
      } else {
        await axios.post(`${API}/tenders/${tenderId}/bookmark`, {}, { withCredentials: true });
        toast.success('Saved to My Tenders');
      }
      fetchAll();
    } catch (e) {
      toast.error('Action failed');
    }
  };

  const handleLogout = async () => {
    // Demo users return to the marketing home page; real users land on /login.
    const wasDemo = Boolean(user?.is_demo_user) || (typeof user?.email === 'string' && user.email.endsWith('.demo@procureoneultra.com'));
    const dest = wasDemo ? '/' : '/login';
    navigate(dest);
    await logout();
  };

  if (!user?.approved) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md p-8">
          <Clock className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold mb-2">Account Pending Approval</h2>
          <p className="text-muted-foreground">Your supplier account is under review.</p>
          <button onClick={handleLogout} className="btn-outline mt-6">Logout</button>
        </div>
      </div>
    );
  }

  const stats = [
    { label: 'Available Tenders', value: tenders.length, icon: FileText, color: 'text-primary' },
    { label: 'My Saved Tenders', value: savedTenders.length, icon: Bookmark, color: 'text-orange' },
    { label: 'My Participated', value: participatedTenders.length, icon: CheckCircle2, color: 'text-green-600' },
    { label: 'Pending Decision', value: participatedTenders.filter(p => p.bid_status === 'submitted').length, icon: Clock, color: 'text-yellow-500' },
  ];

  const TenderCard = ({ t, showBookmark = true, showBidInfo = false }) => (
    <div className="p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors" data-testid={`tender-card-${t.id}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <h3 className="text-lg font-semibold mb-1 text-navy">{t.title}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-muted-foreground">
            <div><span className="font-medium">Category:</span> {t.work_category || 'N/A'}</div>
            <div><span className="font-medium">Location:</span> {t.location || 'N/A'}</div>
            <div><span className="font-medium">Value:</span> ₹{(t.estimated_value || 0).toLocaleString('en-IN')}</div>
            <div><span className="font-medium">Deadline:</span> {t.end_date ? new Date(t.end_date).toLocaleDateString() : 'N/A'}</div>
          </div>
          {t.status && (
            <div className="mt-2"><LifecycleProgressBadge status={t.status} /></div>
          )}
          {showBidInfo && (
            <div className="mt-2 flex items-center gap-3 text-xs">
              <span className="px-2 py-0.5 rounded-full bg-gold/20 text-navy font-semibold">Bid: ₹{(t.quoted_amount || 0).toLocaleString('en-IN')}</span>
              <span className={`px-2 py-0.5 rounded-full font-semibold ${t.bid_status === 'awarded' ? 'bg-green-100 text-green-800' : t.bid_status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-800'}`}>{(t.bid_status || 'submitted').toUpperCase()}</span>
              {t.attachments?.length > 0 && <span className="text-muted-foreground">{t.attachments.length} attachment(s)</span>}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 ml-3">
          <button onClick={() => navigate(`/tender/${t.id}`)} className="btn-primary text-xs" data-testid={`view-tender-${t.id}`}>
            {showBidInfo ? <><Send className="h-3 w-3 inline mr-1" />View</> : 'View'}
          </button>
          {!showBidInfo && t.status === 'published' && (
            <button onClick={() => navigate(`/supplier-dashboard/tender/${t.id}/bid`)} className="px-2 py-1 rounded bg-orange text-white text-xs font-bold inline-flex items-center justify-center" data-testid={`guided-bid-${t.id}`}>
              <Send className="h-3 w-3 mr-1" /> Guided Bid
            </button>
          )}
          {showBookmark && (
            <button onClick={() => toggleBookmark(t.id)} className="text-xs inline-flex items-center justify-center px-2 py-1 rounded border" data-testid={`bookmark-${t.id}`}>
              {savedIds.has(t.id) ? <><BookmarkCheck className="h-3 w-3 mr-1 text-orange" /> Saved</> : <><Bookmark className="h-3 w-3 mr-1" /> Save</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="glass border-b border-border/40 sticky top-0 z-50">
        <nav className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-2xl font-bold tracking-tight text-navy">Supplier Dashboard</h1>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-muted-foreground">{user?.email}</span>
            <button onClick={() => navigate('/privacy')} className="text-xs text-navy underline hover:text-orange hidden sm:inline" data-testid="nav-privacy-btn">Privacy</button>
            <button onClick={handleLogout} className="btn-outline" data-testid="logout-btn">
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </button>
          </div>
        </nav>
      </header>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Profile + Subscription card */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <div className="md:col-span-2 p-5 rounded-lg border border-border bg-card">
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-sm font-semibold text-navy">Supplier Profile</h3>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => navigate('/supplier/negotiations')} className="px-3 py-1.5 rounded bg-gold hover:bg-orange text-navy hover:text-white text-xs font-bold inline-flex items-center transition-colors" data-testid="open-supplier-negotiations-btn">
                  🤝 Negotiations
                </button>
                <button onClick={() => navigate('/supplier-dashboard/profile')} className="px-3 py-1.5 rounded bg-navy text-white text-xs font-bold inline-flex items-center" data-testid="edit-capability-profile-btn">
                  <UserCog className="h-3.5 w-3.5 mr-1" /> Edit Capability Profile
                </button>
              </div>
            </div>
            <ProfilePhotoUploader
              currentUrl={supplierProfile?.logo_url || user?.profile_photo_url}
              label="Company Logo"
              onUploaded={(url) => setSupplierProfile({ ...(supplierProfile || {}), logo_url: url })}
              testid="supplier-logo-uploader"
            />
            {supplierProfile?.company_name && (
              <p className="text-xs text-muted-foreground mt-3">{supplierProfile.company_name} • {supplierProfile.city || ''}</p>
            )}
            {/* Status badges */}
            <div className="flex flex-wrap gap-1.5 mt-3">
              {supplierProfile?.gst_registered === true && (
                <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-[10px] font-bold inline-flex items-center" data-testid="badge-gst-registered">
                  <ShieldCheck className="h-3 w-3 mr-1" /> GST Registered
                </span>
              )}
              {supplierProfile?.gst_registered === false && (
                <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 text-[10px] font-bold" data-testid="badge-gst-unregistered">GST: Unregistered</span>
              )}
              {supplierProfile?.license_available === true && (
                <span className="px-2 py-0.5 rounded-full bg-navy/10 text-navy text-[10px] font-bold inline-flex items-center" data-testid="badge-licensed">
                  <ShieldCheck className="h-3 w-3 mr-1" /> Licensed
                </span>
              )}
              {supplierProfile?.license_available === false && (
                <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 text-[10px] font-bold inline-flex items-center" data-testid="badge-no-license">
                  <ShieldAlert className="h-3 w-3 mr-1" /> No License Declared
                </span>
              )}
              {(supplierProfile?.license_available === null || supplierProfile?.license_available === undefined) && (
                <span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-[10px] font-bold" data-testid="badge-license-pending">License: Not declared yet</span>
              )}
            </div>
          </div>
          <div className="p-5 rounded-lg border border-gold/40 bg-gold/5">
            <h3 className="text-sm font-semibold text-navy mb-2">Membership</h3>
            {subscription?.id ? (
              <>
                <div className="text-xs text-muted-foreground">Status</div>
                <div className="text-lg font-bold text-green-700 capitalize">{subscription.payment_status}</div>
                {subscription.end_date && <p className="text-xs text-muted-foreground mt-1">Valid till: {new Date(subscription.end_date).toLocaleDateString()}</p>}
                {subscription.is_founding && <span className="mt-2 inline-block px-2 py-0.5 rounded text-xs font-bold bg-gold/30 text-navy">★ Founding Member</span>}
              </>
            ) : (
              <>
                <div className="text-xs text-muted-foreground mb-2">No active subscription</div>
                <button onClick={() => navigate('/checkout?plan=supplier_yearly')} className="w-full px-3 py-2 rounded bg-navy text-white text-sm font-semibold inline-flex items-center justify-center" data-testid="subscribe-cta-supplier">
                  <CreditCard className="h-3 w-3 mr-2" /> Subscribe
                </button>
              </>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          {stats.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="p-5 rounded-lg border border-border bg-card">
                <Icon className={`h-7 w-7 ${s.color} mb-2`} />
                <div className="text-2xl font-bold text-navy">{s.value}</div>
                <div className="text-xs text-muted-foreground">{s.label}</div>
              </div>
            );
          })}
        </div>

        {/* SUPPLIER V1 · Phase B — Success Fee + PO Access tiles */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <button onClick={() => navigate('/supplier-dashboard/success-fees')} className={`text-left p-5 rounded-lg border-2 transition-all ${pendingSuccessFees.length > 0 ? 'border-orange bg-orange/10' : 'border-border bg-card'}`} data-testid="dash-pending-fees-tile">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Pending PO Fee Payments</div>
              <Receipt className={`h-5 w-5 ${pendingSuccessFees.length > 0 ? 'text-orange' : 'text-muted-foreground'}`} />
            </div>
            <div className={`text-2xl font-bold ${pendingSuccessFees.length > 0 ? 'text-orange' : 'text-navy'}`} data-testid="dash-pending-fees-count">{pendingSuccessFees.length}</div>
            <div className="text-[11px] text-muted-foreground mt-1">{pendingSuccessFees.length > 0 ? 'Pay to unlock your Purchase Orders' : 'No pending payments'}</div>
          </button>
          <button onClick={() => navigate('/supplier-dashboard/success-fees')} className="text-left p-5 rounded-lg border-2 border-green-300 bg-green-50/30" data-testid="dash-available-pos-tile">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Available Purchase Orders</div>
              <CheckCircle2 className="h-5 w-5 text-green-700" />
            </div>
            <div className="text-2xl font-bold text-green-700" data-testid="dash-available-pos-count">{paidSuccessFees.length}</div>
            <div className="text-[11px] text-muted-foreground mt-1">{paidSuccessFees.length > 0 ? 'PO download unlocked' : 'POs will appear once fees are paid'}</div>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex flex-nowrap overflow-x-auto border-b border-border mb-6 -mx-2 px-2 scrollbar-hide">
          {[
            { key: 'available', label: 'Available Tenders', icon: Search, count: tenders.length },
            { key: 'saved', label: 'My Tenders (Saved)', icon: Bookmark, count: savedTenders.length },
            { key: 'participated', label: 'My Participated', icon: CheckCircle2, count: participatedTenders.length },
            { key: 'invoices', label: 'Invoices & Receipts', icon: Receipt, count: null },
          ].map(t => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-3 sm:px-5 py-3 text-xs sm:text-sm font-semibold inline-flex items-center whitespace-nowrap ${active ? 'text-navy border-b-2 border-orange' : 'text-muted-foreground hover:text-navy'}`}
                data-testid={`supplier-tab-${t.key}`}
              >
                <Icon className="h-4 w-4 mr-1.5 sm:mr-2" /> <span>{t.label}</span>
                {t.count !== null && <span className="ml-2 px-2 py-0.5 rounded-full bg-muted text-xs">{t.count}</span>}
              </button>
            );
          })}
        </div>

        {tab === 'available' && (
          <div>
            <div className="bg-card border border-border rounded-lg p-5 mb-5">
              <div className="flex items-center mb-3"><Filter className="h-4 w-4 text-primary mr-2" /><h2 className="text-sm font-semibold">Filter Tenders</h2></div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <input type="text" placeholder="Category" value={filters.category} onChange={e => setFilters({ ...filters, category: e.target.value })} className="px-3 py-2 rounded border border-input bg-background text-sm" data-testid="filter-category-input" />
                <input type="text" placeholder="Location" value={filters.location} onChange={e => setFilters({ ...filters, location: e.target.value })} className="px-3 py-2 rounded border border-input bg-background text-sm" data-testid="filter-location-input" />
                <input type="number" placeholder="Min Value" value={filters.minValue} onChange={e => setFilters({ ...filters, minValue: e.target.value })} className="px-3 py-2 rounded border border-input bg-background text-sm" data-testid="filter-min-value-input" />
                <input type="number" placeholder="Max Value" value={filters.maxValue} onChange={e => setFilters({ ...filters, maxValue: e.target.value })} className="px-3 py-2 rounded border border-input bg-background text-sm" data-testid="filter-max-value-input" />
              </div>
              <button onClick={refetchTenders} className="btn-primary mt-3 text-sm" data-testid="apply-filters-btn"><Search className="h-3 w-3 mr-1" /> Apply</button>
            </div>
            {loading ? <div className="text-center py-8">Loading...</div> : tenders.length === 0 ? <EmptyState text="No tenders available right now." /> : (
              <div className="space-y-3">{tenders.map(t => <TenderCard key={t.id} t={t} />)}</div>
            )}
          </div>
        )}

        {tab === 'saved' && (
          <div data-testid="saved-tenders-list">
            {savedTenders.length === 0 ? <EmptyState text="You haven't saved any tenders yet. Bookmark interesting tenders from the Available tab." /> : (
              <div className="space-y-3">{savedTenders.map(t => <TenderCard key={t.id} t={t} />)}</div>
            )}
          </div>
        )}

        {tab === 'participated' && (
          <div data-testid="participated-tenders-list">
            {participatedTenders.length === 0 ? <EmptyState text="You haven't submitted any quotations yet. Submit a bid from a tender to track it here." /> : (
              <div className="space-y-3">{participatedTenders.map(t => <TenderCard key={t.id} t={t} showBookmark={false} showBidInfo />)}</div>
            )}
          </div>
        )}

        {tab === 'invoices' && (
          <div data-testid="supplier-invoices-tab">
            <h2 className="text-base font-semibold text-navy mb-3">Invoices & Payment Receipts</h2>
            <MyInvoices />
          </div>
        )}
      </div>
      <ComplianceFooter compact className="mt-4 border-t border-border pt-4 pb-16" />
      <SupplierBottomNav />
    </div>
  );
};

const EmptyState = ({ text }) => (
  <div className="text-center py-12 text-muted-foreground bg-card rounded-lg border border-dashed border-border">
    <FileText className="h-12 w-12 mx-auto mb-3 opacity-40" />
    <p className="text-sm">{text}</p>
  </div>
);

export default SupplierDashboard;
